import { customEvent } from '@fe-toolkit/eventing'

import { PlanCard } from '@giffgaff/broadband-common'

import { Badge, Box, theme, Typography } from '@fe-toolkit/web-components/ssr'

import getProductEventEntity from '@/utils/eventing/getProductEventEntity'

import { useEffectOnceWithCondition } from '@/hooks/useEffectOnceWithCondition'

import { FormattedProductForPlan, SimpleCurrentPlan } from '../types'

export default function ChangePlanSelectionProductCard({
  product,
  currentPlan,
}: Readonly<{
  product: FormattedProductForPlan
  currentPlan: SimpleCurrentPlan
}>) {
  useEffectOnceWithCondition(
    ([product, currentPlan]) => {
      customEvent('bb_product_impression', {
        product: getProductEventEntity(product),
        currentPlanId: currentPlan?.id ?? '',
      })
    },
    ([_, currentPlan]) => {
      return currentPlan.id !== undefined
    },
    [product, currentPlan] as const,
  )

  const isPromotional = !!product.promotionalProduct

  return (
    <PlanCard
      badgeContent={
        product.badgeContent ? (
          <Badge
            backgroundColor={product.badgeContent.backgroundColor}
            color={product.badgeContent.color}
            variant="murmurBold"
          >
            {product.badgeContent.text}
          </Badge>
        ) : undefined
      }
      collapsibleHighlights={true}
      displayName={product.displayName}
      headerEnd={product.bottomSlot}
      headerStart={
        isPromotional && !product.hasActivePlan ? (
          <Box backgroundColor={theme.colors.pink} px={theme.spacing.small} py={theme.spacing.tiny} textAlign="center">
            <Typography as="p" variant="whisperLoud">
              Limited time offer
            </Typography>
          </Box>
        ) : null
      }
      highlights={product.benefits && product.benefits.length > 0 ? product.benefits : undefined}
      initialHighlightsExpanded={false}
      price={
        isPromotional ? (
          <>
            <Typography strikeThrough as="span" variant="loud">
              {product.formattedPostPromotionPrice}
            </Typography>{' '}
            <span data-testid="price">{product.formattedPrice}/mo</span>
          </>
        ) : (
          <span data-testid="price">{product.formattedPrice}/mo</span>
        )
      }
      priceNote={
        isPromotional ? (
          <span data-testid="promo-terms">
            for {product.promotionPeriodInMonths} months then {product.formattedPostPromotionPrice}/mo
          </span>
        ) : undefined
      }
      speed={product.formattedSpeed}
      speedNote={isPromotional ? product.contractDurationText : undefined}
      sx={() =>
        isPromotional && !product.hasActivePlan
          ? {
              backgroundColor: theme.colors.pink,
              borderColor: theme.colors.pink,
              ['& .plan-card__top']: {
                backgroundColor: theme.colors.wildSand,
                borderTopLeftRadius: theme.borderRadius.small,
                borderTopRightRadius: theme.borderRadius.small,
              },
            }
          : {}
      }
    />
  )
}
